/** Merge under `styleGallery` in each locale (see app/i18n.ts). */
export const styleGalleryCopy = {
  en: {
    eyebrow: 'Templates',
    title: 'Find your presentation style',
    description: 'Choose a visual direction. You can customize the theme later.',
    contextNav: 'Templates and styles',
    tabTemplates: 'Templates',
    tabStyles: 'Styles',
    filtersLabel: 'Find a style',
    search: 'Search styles',
    searchPlaceholder: 'Search styles',
    purposeLabel: 'Use case',
    moodLabel: 'Mood',
    motionLabel: 'Motion intensity',
    sortLabel: 'Sort',
    allPurposes: 'All use cases',
    allMoods: 'All moods',
    allMotion: 'Any motion',
    accessibleOnly: 'Accessible only',
    clearFilters: 'Clear filters',
    sort: { name: 'Name', motion: 'Calmest first' },
    purpose: {
      pitch: 'Pitch',
      report: 'Report',
      product: 'Product',
      story: 'Story',
      training: 'Training',
    },
    mood: {
      calm: 'Calm',
      bold: 'Bold',
      playful: 'Playful',
      editorial: 'Editorial',
      cinematic: 'Cinematic',
      technical: 'Technical',
    },
    motion: {
      none: 'No motion',
      subtle: 'Subtle motion',
      expressive: 'Expressive motion',
      cinematic: 'Cinematic motion',
    },
    results_one: '{{count}} style',
    results_other: '{{count}} styles',
    listLabel: 'Styles',
    cardHint: 'Hover to preview, click to select',
    selected: 'Selected',
    previewing: 'Previewing',
    previewingHint: 'Temporary preview. Click a style to keep it selected.',
    origin: { official: 'Official', company: 'Company' },
    version: 'Version {{version}}',
    availability: {
      available: 'Available',
      'preview-only': 'Preview only',
      unavailable: 'Unavailable',
    },
    reason: {
      'concept-only': 'This style is a concept. Slider does not support it yet.',
      'not-enough-templates': 'Not enough compatible templates for this style yet.',
      'not-in-project-kit': 'This style’s templates are not in your project’s design kit.',
      'missing-assets': 'Some assets this style needs are not ready.',
      retired: 'This style version was retired and cannot be chosen for new presentations.',
      'eligibility-unconfirmed':
        'This preview shows a real template. Whether you can use it in a project is confirmed separately.',
    },
    reasonFallback: 'This style cannot be used right now.',
    badgeAccessible: 'Accessible',
    badgeRtl: 'RTL-ready',
    templateCount_one: '{{count}} compatible template',
    templateCount_other: '{{count}} compatible templates',
    compare: {
      addAction: 'Add to comparison',
      removeAction: 'Remove from comparison',
      addShort: 'Compare',
      removeShort: 'Remove',
      selectionLabel: 'Styles selected for comparison',
      count_one: '{{count}} of 2 styles selected',
      count_other: '{{count}} of 2 styles selected',
      chooseAnother: 'Choose one more style to compare.',
      readyHint: 'Ready to compare.',
      remove: 'Remove {{name}} from comparison',
      open: 'Compare styles',
      title: 'Compare styles',
      description: 'Details from your current authorized style catalog.',
      readOnly: 'Read-only. Comparing styles does not apply a theme or create a presentation.',
      limit: 'Two styles selected. Remove one to compare a different style.',
      hidden: 'A compared style is hidden by your filters, but remains in the comparison.',
      close: 'Close comparison',
      version: 'Version',
      origin: 'Origin',
      availability: 'Availability',
      reason: 'Availability reason',
      motion: 'Motion intensity',
      templates: 'Compatible templates',
      components: 'Declared components',
      requirements: 'Asset requirements',
      rtl: 'Verified RTL support',
      accessibility: 'Verified accessibility',
      swatches: 'Style colours',
      verified: 'Verified',
      notVerified: 'Not verified',
      notApplicable: 'Not applicable',
      notRecorded: 'Not recorded',
      different: 'Different',
      slot: 'Compare {{slot}}',
      emptySlot: 'Choose another',
      clear: 'Clear',
      sideBySide: 'Side by side',
      groupIdentity: 'Identity',
      groupAvailability: 'Availability & evidence',
      groupDesign: 'Visual direction',
      groupRequirements: 'Compatibility & assets',
      differencesOnly: 'Differences only',
      differenceCount_one: '{{count}} difference',
      differenceCount_other: '{{count}} differences',
      groupChanges_one: '{{count}} difference',
      groupChanges_other: '{{count}} differences',
      noDifferences: 'No differences are recorded for these styles.',
    },
    detail: {
      heading: 'Style details',
      previewLabel: 'Style preview',
      fixturesLabel: 'Sample slides',
      fixtureOption: 'Show sample: {{name}}',
      runtimeNote: 'Live preview using sample content. Nothing is saved.',
      posterNote: 'Illustration only. This is not a live preview of a template.',
      noPreview: 'No preview is available for this style.',
      previewFailed: 'This preview could not be loaded.',
      runtimeUnavailable: 'The live preview could not be shown. Nothing was changed.',
      about: 'About this style',
      motionHeading: 'Motion',
      componentsHeading: 'Components',
      componentsNone: 'No interactive components declared.',
      requirementsHeading: 'Requirements',
      requirementsNone: 'No special requirements.',
      selectionNote: 'Selecting a style does not change your theme or create a presentation.',
      useStyle: 'Use this style',
      useStyleHint: 'Includes compatible layouts and motion choices.',
      reducedMotionNote: 'Reduced motion is on, so previews stay still.',
      swatches: 'Style colours',
      sheetDescription: 'Details for the style you selected.',
    },
    empty: {
      title: 'No styles yet',
      description: 'Styles you can choose from will appear here.',
    },
    filterEmpty: {
      title: 'No styles match',
      description: 'Try a different search or clear the filters.',
    },
    error: {
      title: 'Styles could not be loaded',
      description: 'Check your connection and try again.',
      retry: 'Retry',
    },
    loading: 'Loading styles',
    noSelection: 'Select a style to see its details.',
    hidden: {
      body: 'is selected, but your filters hide it from the results.',
      reset: 'Clear filters to show it',
    },
    selectedBar: {
      label: 'Selected style',
      viewDetails: 'View details',
      close: 'Close details',
    },
  },
  fa: {
    eyebrow: 'قالب‌ها',
    title: 'سبک ارائهٔ خود را پیدا کنید',
    description: 'یک جهت بصری انتخاب کنید. بعداً می‌توانید تم را شخصی‌سازی کنید.',
    contextNav: 'قالب‌ها و سبک‌ها',
    tabTemplates: 'قالب‌ها',
    tabStyles: 'سبک‌ها',
    filtersLabel: 'یافتن سبک',
    search: 'جستجوی سبک‌ها',
    searchPlaceholder: 'جستجوی سبک‌ها',
    purposeLabel: 'کاربرد',
    moodLabel: 'حال‌وهوا',
    motionLabel: 'شدت حرکت',
    sortLabel: 'مرتب‌سازی',
    allPurposes: 'همهٔ کاربردها',
    allMoods: 'همهٔ حال‌وهواها',
    allMotion: 'هر میزان حرکت',
    accessibleOnly: 'فقط دسترس‌پذیر',
    clearFilters: 'پاک کردن فیلترها',
    sort: { name: 'نام', motion: 'آرام‌ترین‌ها اول' },
    purpose: {
      pitch: 'ارائهٔ سرمایه‌گذاری',
      report: 'گزارش',
      product: 'محصول',
      story: 'روایت',
      training: 'آموزش',
    },
    mood: {
      calm: 'آرام',
      bold: 'جسور',
      playful: 'بازیگوش',
      editorial: 'مجله‌ای',
      cinematic: 'سینمایی',
      technical: 'فنی',
    },
    motion: {
      none: 'بدون حرکت',
      subtle: 'حرکت ملایم',
      expressive: 'حرکت پویا',
      cinematic: 'حرکت سینمایی',
    },
    results_one: '{{count}} سبک',
    results_other: '{{count}} سبک',
    listLabel: 'سبک‌ها',
    cardHint: 'برای پیش‌نمایش اشاره کنید، برای انتخاب کلیک کنید',
    selected: 'انتخاب‌شده',
    previewing: 'در حال پیش‌نمایش',
    previewingHint: 'پیش‌نمایش موقت. برای ماندگار شدن انتخاب روی یک سبک کلیک کنید.',
    origin: { official: 'رسمی', company: 'شرکت' },
    version: 'نسخهٔ {{version}}',
    availability: {
      available: 'در دسترس',
      'preview-only': 'فقط پیش‌نمایش',
      unavailable: 'در دسترس نیست',
    },
    reason: {
      'concept-only': 'این سبک فعلاً یک مفهوم است و اسلایدر هنوز از آن پشتیبانی نمی‌کند.',
      'not-enough-templates': 'هنوز قالب سازگار کافی برای این سبک وجود ندارد.',
      'not-in-project-kit': 'قالب‌های این سبک در کیت طراحی پروژهٔ شما نیستند.',
      'missing-assets': 'برخی دارایی‌های موردنیاز این سبک آماده نیستند.',
      retired: 'این نسخهٔ سبک بازنشسته شده و برای ارائه‌های جدید قابل انتخاب نیست.',
      'eligibility-unconfirmed':
        'این پیش‌نمایش یک قالب واقعی را نشان می‌دهد. امکان استفاده از آن در پروژه جداگانه تأیید می‌شود.',
    },
    reasonFallback: 'این سبک در حال حاضر قابل استفاده نیست.',
    badgeAccessible: 'دسترس‌پذیر',
    badgeRtl: 'مناسب راست‌به‌چپ',
    templateCount_one: '{{count}} قالب سازگار',
    templateCount_other: '{{count}} قالب سازگار',
    compare: {
      addAction: 'افزودن به مقایسه',
      removeAction: 'حذف از مقایسه',
      addShort: 'مقایسه',
      removeShort: 'حذف',
      selectionLabel: 'سبک‌های انتخاب‌شده برای مقایسه',
      count_one: '{{count}} سبک از ۲ سبک انتخاب شده',
      count_other: '{{count}} سبک از ۲ سبک انتخاب شده',
      chooseAnother: 'یک سبک دیگر برای مقایسه انتخاب کنید.',
      readyHint: 'آمادهٔ مقایسه است.',
      remove: 'حذف {{name}} از مقایسه',
      open: 'مقایسهٔ سبک‌ها',
      title: 'مقایسهٔ سبک‌ها',
      description: 'جزئیات بر اساس فهرست سبک‌های مجاز فعلی شما.',
      readOnly: 'فقط مشاهده. مقایسه، تم را اعمال نمی‌کند و ارائه‌ای نمی‌سازد.',
      limit: 'دو سبک انتخاب شده‌اند. برای مقایسهٔ سبک دیگری، یکی را حذف کنید.',
      hidden: 'یکی از سبک‌های مقایسه‌شده با فیلترها پنهان است، اما در مقایسه باقی می‌ماند.',
      close: 'بستن مقایسه',
      version: 'نسخه',
      origin: 'منبع',
      availability: 'دسترسی',
      reason: 'دلیل وضعیت دسترسی',
      motion: 'شدت حرکت',
      templates: 'قالب‌های سازگار',
      components: 'اجزای اعلام‌شده',
      requirements: 'نیازمندی‌های دارایی',
      rtl: 'پشتیبانی تأییدشدهٔ راست‌به‌چپ',
      accessibility: 'دسترس‌پذیری تأییدشده',
      swatches: 'رنگ‌های سبک',
      verified: 'تأیید شده',
      notVerified: 'تأیید نشده',
      notApplicable: 'صدق نمی‌کند',
      notRecorded: 'ثبت نشده',
      different: 'متفاوت',
      slot: 'مقایسه {{slot}}',
      emptySlot: 'سبک دوم را انتخاب کنید',
      clear: 'پاک کردن',
      sideBySide: 'مقایسه رو در رو',
      groupIdentity: 'هویت استایل',
      groupAvailability: 'دسترسی و شواهد',
      groupDesign: 'جهت بصری',
      groupRequirements: 'سازگاری و دارایی‌ها',
      differencesOnly: 'فقط تفاوت‌ها',
      differenceCount_one: '{{count}} تفاوت',
      differenceCount_other: '{{count}} تفاوت',
      groupChanges_one: '{{count}} تفاوت',
      groupChanges_other: '{{count}} تفاوت',
      noDifferences: 'تفاوت ثبت‌شده‌ای بین این دو سبک وجود ندارد.',
    },
    detail: {
      heading: 'جزئیات سبک',
      previewLabel: 'پیش‌نمایش سبک',
      fixturesLabel: 'اسلایدهای نمونه',
      fixtureOption: 'نمایش نمونه: {{name}}',
      runtimeNote: 'پیش‌نمایش زنده با محتوای نمونه. چیزی ذخیره نمی‌شود.',
      posterNote: 'فقط تصویر نمونه است و پیش‌نمایش زندهٔ یک قالب نیست.',
      noPreview: 'برای این سبک پیش‌نمایشی در دسترس نیست.',
      previewFailed: 'این پیش‌نمایش بارگذاری نشد.',
      runtimeUnavailable: 'پیش‌نمایش زنده نمایش داده نشد. چیزی تغییر نکرد.',
      about: 'دربارهٔ این سبک',
      motionHeading: 'حرکت',
      componentsHeading: 'اجزا',
      componentsNone: 'جزء تعاملی اعلام نشده است.',
      requirementsHeading: 'پیش‌نیازها',
      requirementsNone: 'پیش‌نیاز خاصی وجود ندارد.',
      selectionNote: 'انتخاب سبک، تم شما را تغییر نمی‌دهد و ارائه‌ای نمی‌سازد.',
      useStyle: 'استفاده از این سبک',
      useStyleHint: 'شامل چیدمان‌ها و حرکت‌های سازگار است.',
      reducedMotionNote: 'کاهش حرکت روشن است، پس پیش‌نمایش‌ها ثابت می‌مانند.',
      swatches: 'رنگ‌های سبک',
      sheetDescription: 'جزئیات سبکی که انتخاب کرده‌اید.',
    },
    empty: {
      title: 'هنوز سبکی نیست',
      description: 'سبک‌هایی که می‌توانید انتخاب کنید اینجا نمایش داده می‌شوند.',
    },
    filterEmpty: {
      title: 'سبکی یافت نشد',
      description: 'جستجوی دیگری امتحان کنید یا فیلترها را پاک کنید.',
    },
    error: {
      title: 'سبک‌ها بارگذاری نشدند',
      description: 'اتصال خود را بررسی کنید و دوباره تلاش کنید.',
      retry: 'تلاش دوباره',
    },
    loading: 'در حال بارگذاری سبک‌ها',
    noSelection: 'برای دیدن جزئیات، یک سبک انتخاب کنید.',
    hidden: {
      body: 'انتخاب شده است، اما فیلترهای شما آن را از نتایج پنهان کرده‌اند.',
      reset: 'پاک کردن فیلترها برای نمایش آن',
    },
    selectedBar: {
      label: 'سبک انتخاب‌شده',
      viewDetails: 'مشاهدهٔ جزئیات',
      close: 'بستن جزئیات',
    },
  },
} as const;
